import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ResourceCenter = () => {
  const [activeCategory, setActiveCategory] = useState('training');

  const categories = [
    { id: 'training', name: 'Training Materials', icon: 'GraduationCap' },
    { id: 'guides', name: 'Best Practices', icon: 'BookOpen' },
    { id: 'tools', name: 'Digital Tools', icon: 'Smartphone' },
    { id: 'support', name: 'Technical Support', icon: 'Headphones' }
  ];

  const resources = {
    training: [
      {
        id: 1,
        title: "Roofing Safety Certification Course",
        description: "Comprehensive OSHA-compliant safety training covering fall protection, ladder safety, and hazard identification.",
        type: "Video Course",
        duration: "4 hours",
        level: "Required",
        status: "Available",
        icon: "Shield"
      },
      {
        id: 2,
        title: "Customer Communication Excellence",
        description: "Learn effective communication strategies to build trust, manage expectations, and ensure customer satisfaction.",
        type: "Interactive Module",
        duration: "2 hours",
        level: "Recommended",
        status: "Available",
        icon: "MessageSquare"
      },
      {
        id: 3,
        title: "Advanced Installation Techniques",
        description: "Master advanced roofing installation methods for various materials including metal, tile, and specialty systems.",
        type: "Video Series",
        duration: "6 hours",
        level: "Advanced",
        status: "Available",
        icon: "Tool"
      },
      {
        id: 4,
        title: "Quality Control & Inspection",
        description: "Detailed quality standards and inspection procedures to ensure every project meets OpenbooQ standards.",
        type: "Checklist Guide",
        duration: "1 hour",
        level: "Required",
        status: "Available",
        icon: "CheckSquare"
      }
    ],
    guides: [
      {
        id: 1,
        title: "Project Management Best Practices",
        description: "Streamline your workflow with proven project management techniques and timeline optimization strategies.",
        type: "PDF Guide",
        duration: "30 min read",
        level: "Essential",
        status: "Available",
        icon: "Calendar"
      },
      {
        id: 2,
        title: "Material Selection Guide",
        description: "Comprehensive guide to choosing the right materials for different climates, budgets, and customer preferences.",
        type: "Interactive Guide",
        duration: "45 min",
        level: "Recommended",
        status: "Available",
        icon: "Layers"
      },
      {
        id: 3,
        title: "Weather-Dependent Scheduling",
        description: "Optimize your scheduling around weather patterns and seasonal considerations for maximum efficiency.",
        type: "Planning Tool",
        duration: "20 min",
        level: "Helpful",
        status: "Available",
        icon: "Cloud"
      },
      {
        id: 4,
        title: "Warranty & Maintenance Guidelines",
        description: "Complete guide to warranty procedures, maintenance schedules, and customer follow-up protocols.",
        type: "Reference Manual",
        duration: "1 hour",
        level: "Required",
        status: "Available",
        icon: "FileText"
      }
    ],
    tools: [
      {
        id: 1,
        title: "OpenbooQ Contractor App",
        description: "Mobile app for job management, customer communication, photo documentation, and real-time project tracking.",
        type: "Mobile App",
        duration: "Always Available",
        level: "Required",
        status: "Download",
        icon: "Smartphone"
      },
      {
        id: 2,
        title: "Measurement & Estimation Tool",
        description: "Accurate roof measurement and material estimation using satellite imagery and AI-powered calculations.",
        type: "Web Tool",
        duration: "Per Project",
        level: "Essential",
        status: "Available",
        icon: "Ruler"
      },
      {
        id: 3,
        title: "Customer Portal Integration",
        description: "Seamlessly connect with customers through integrated messaging, photo sharing, and progress updates.",
        type: "Platform Feature",
        duration: "Ongoing",
        level: "Recommended",
        status: "Available",
        icon: "Users"
      },
      {
        id: 4,
        title: "Invoice & Payment Processing",
        description: "Automated invoicing system with integrated payment processing and financial tracking capabilities.",
        type: "Financial Tool",
        duration: "Per Transaction",
        level: "Required",
        status: "Available",
        icon: "CreditCard"
      }
    ],
    support: [
      {
        id: 1,
        title: "24/7 Technical Helpdesk",
        description: "Round-the-clock technical support for app issues, platform questions, and troubleshooting assistance.",
        type: "Live Support",
        duration: "Immediate",
        level: "Available",
        status: "Contact",
        icon: "Headphones"
      },
      {
        id: 2,
        title: "Contractor Success Manager",
        description: "Dedicated success manager to help optimize your performance, resolve issues, and grow your business.",
        type: "Personal Support",
        duration: "Scheduled",
        level: "Premium",
        status: "Available",
        icon: "User"
      },
      {
        id: 3,
        title: "Community Forum",
        description: "Connect with other contractors, share experiences, ask questions, and learn from the community.",
        type: "Online Community",
        duration: "Always Open",
        level: "Open Access",
        status: "Join",
        icon: "MessageCircle"
      },
      {
        id: 4,
        title: "Training Webinars",
        description: "Regular live training sessions covering new features, industry updates, and skill development.",
        type: "Live Sessions",
        duration: "1 hour",
        level: "Optional",
        status: "Register",
        icon: "Video"
      }
    ]
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Required': return 'bg-error/10 text-error';
      case 'Essential': return 'bg-warning/10 text-warning';
      case 'Recommended': return 'bg-primary/10 text-primary';
      case 'Advanced': return 'bg-secondary/10 text-secondary';
      case 'Premium': return 'bg-accent/10 text-accent';
      default: return 'bg-surface text-text-secondary';
    }
  };

  const getStatusButton = (status) => {
    switch (status) {
      case 'Available': return { variant: 'default', text: 'Access Now' };
      case 'Download': return { variant: 'success', text: 'Download' };
      case 'Contact': return { variant: 'outline', text: 'Contact Support' };
      case 'Join': return { variant: 'secondary', text: 'Join Forum' };
      case 'Register': return { variant: 'outline', text: 'Register' };
      default: return { variant: 'default', text: 'Learn More' };
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center space-y-6 mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full">
            <Icon name="BookOpen" size={16} className="text-primary mr-2" />
            <span className="text-sm font-medium text-primary">Resource Center</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-jakarta font-bold text-text-primary">
            Everything You Need to Succeed
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Access comprehensive training materials, best practice guides, digital tools, 
            and technical support to excel as an OpenbooQ contractor.
          </p>
        </div>

        {/* Category Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setActiveCategory(category?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeCategory === category?.id
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white text-text-primary hover:bg-primary/10 hover:text-primary border border-border'
              }`}
            >
              <Icon name={category?.icon} size={16} />
              <span>{category?.name}</span>
            </button>
          ))}
        </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {resources?.[activeCategory]?.map((resource) => (
            <div 
              key={resource?.id}
              className="bg-white border border-border rounded-xl p-6 hover:shadow-trust transition-all duration-300"
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                      <Icon name={resource?.icon} size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-jakarta font-semibold text-text-primary">{resource?.title}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-text-secondary">{resource?.type}</span>
                        <span className="text-xs text-text-secondary">•</span>
                        <span className="text-xs text-text-secondary">{resource?.duration}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLevelColor(resource?.level)}`}>
                    {resource?.level}
                  </span>
                </div>
                
                <p className="text-text-secondary text-sm leading-relaxed">
                  {resource?.description}
                </p>
                
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-text-secondary" />
                    <span className="text-xs text-text-secondary">{resource?.duration}</span>
                  </div>
                  <Button 
                    variant={getStatusButton(resource?.status)?.variant}
                    size="sm"
                  >
                    {getStatusButton(resource?.status)?.text}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Access Panel */}
        <div className="mt-16 bg-white rounded-2xl shadow-trust p-8 lg:p-12">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <h3 className="text-2xl font-jakarta font-bold text-text-primary">
                Quick Access Panel
              </h3>
              <p className="text-text-secondary">
                Get instant access to the most commonly used resources and tools for your daily operations.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <button className="flex items-center space-x-3 p-4 bg-surface rounded-lg hover:bg-primary/10 transition-colors duration-200 text-left">
                  <Icon name="Smartphone" size={20} className="text-primary" />
                  <div>
                    <div className="font-medium text-text-primary">Contractor App</div>
                    <div className="text-sm text-text-secondary">Mobile job management</div>
                  </div>
                </button>
                
                <button className="flex items-center space-x-3 p-4 bg-surface rounded-lg hover:bg-primary/10 transition-colors duration-200 text-left">
                  <Icon name="Headphones" size={20} className="text-primary" />
                  <div>
                    <div className="font-medium text-text-primary">Support Chat</div>
                    <div className="text-sm text-text-secondary">24/7 assistance</div>
                  </div>
                </button>
                
                <button className="flex items-center space-x-3 p-4 bg-surface rounded-lg hover:bg-primary/10 transition-colors duration-200 text-left">
                  <Icon name="MessageCircle" size={20} className="text-primary" />
                  <div>
                    <div className="font-medium text-text-primary">Community Forum</div>
                    <div className="text-sm text-text-secondary">Connect with peers</div>
                  </div>
                </button>
                
                <button className="flex items-center space-x-3 p-4 bg-surface rounded-lg hover:bg-primary/10 transition-colors duration-200 text-left">
                  <Icon name="Calendar" size={20} className="text-primary" />
                  <div>
                    <div className="font-medium text-text-primary">Training Schedule</div>
                    <div className="text-sm text-text-secondary">Upcoming webinars</div>
                  </div>
                </button>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6">
                <h4 className="font-jakarta font-semibold text-text-primary mb-4">Need Help Getting Started?</h4>
                <p className="text-sm text-text-secondary mb-4">
                  Our onboarding team is ready to help you navigate the platform and get up to speed quickly.
                </p>
                <Button variant="default" size="sm" fullWidth>
                  Schedule Onboarding Call
                </Button>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-jakarta font-bold text-primary">98%</div>
                <div className="text-sm text-text-secondary">Contractor satisfaction rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourceCenter;